import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/axios";

export default function TotpSetup() {
	const { user, refreshUser } = useAuth();
	const navigate = useNavigate();
	
	const [step, setStep] = useState("loading"); // loading | qr | verify | done
	const [qrDataUrl, setQrDataUrl] = useState("");
	const [manualKey, setManualKey] = useState("");
	const [showManual, setShowManual] = useState(false);
	const [code, setCode] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	
	useEffect(() => {
		// If the user already has TOTP enabled, nothing to do here
		if (user?.totpEnabled) {
			navigate("/account");
			return;
		}
		startSetup();

	}, []);
	
	const startSetup = async () => {
		setStep("loading");
		setError("");
		try {
			const { data } = await api.post("/auth/totp/setup");
			setQrDataUrl(data.qrDataUrl);
			setManualKey(data.manualEntryKey);
			setStep("qr");
		} catch (err) {
			setError(
				err.response?.data?.error ||
				"Failed to start 2FA setup. Please try again."
			);
			setStep("qr");
		}
	};
	
	const handleVerify = async (e) => {
		e.preventDefault();
		setError("");
		setLoading(true);
		try {
			const { data } = await api.post("/auth/totp/verify-setup", { code });
			// Update stored token with the full session token
			localStorage.setItem("cff_token", data.token);
			await refreshUser();
			setStep("done");
		} catch (err) {
			setError(
				err.response?.data?.error ||
				"Invalid code. Please try again."
			);
			setCode("");
		} finally {
			setLoading(false);
		}
	};
	
	if (step === "loading") {
		return (
			<div className="flex justify-center items-center h-48">
			<div className="animate-spin h-10 w-10 rounded-full border-4
			border-farm-green border-t-transparent" />
			</div>
		);
	}
	
	if (step === "done") {
		return (
			<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8
			text-center border border-farm-tan/30
			dark:border-gray-700 max-w-sm mx-auto">
			<div className="text-6xl mb-4">🎉</div>
			<h2 className="text-2xl font-bold text-farm-green dark:text-green-400
			mb-3">
			2FA Enabled!
			</h2>
			<p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
			Two-factor authentication is now active on your account. You will
			need your authenticator app every time you sign in.
			</p>
			<button
			onClick={() => navigate("/order")}
			className="w-full bg-farm-green text-white py-3 rounded-xl
			font-semibold hover:bg-green-700 transition-colors"
			>
			Continue to App →
			</button>
			</div>
		);
	}
	
	return (
		<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8
		border border-farm-tan/30 dark:border-gray-700
		transition-colors duration-200 max-w-sm mx-auto">
		
		{/* ── Step indicator ── */}
		<div className="flex items-center justify-center gap-2 mb-6">
		<StepDot n={1} active={step === "qr"} done={step === "verify"} />
		<div className="h-px w-8 bg-gray-200 dark:bg-gray-600" />
		<StepDot n={2} active={step === "verify"} done={false} />
		</div>
		
		{step === "qr" && (
			<>
			<div className="text-center mb-5">
			<h2 className="text-2xl font-bold text-farm-green
			dark:text-green-400">
			Set Up Two-Factor Auth
			</h2>
			<p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
			Scan the QR code below with your authenticator app, then
			click <strong>Next</strong>.
			</p>
			</div>
			
			{error && (
				<div className="bg-red-50 dark:bg-red-900/30 border
				border-red-300 dark:border-red-600 text-red-700
				dark:text-red-300 rounded-lg p-3 mb-4 text-sm">
				{error}
				</div>
			)}
			
			{/* QR code */}
			{qrDataUrl && (
				<div className="flex justify-center mb-4">
				<div className="bg-white p-3 rounded-xl border-2
				border-gray-200 dark:border-gray-600
				inline-block">
				<img
				src={qrDataUrl}
				alt="TOTP QR code"
				className="h-48 w-48"
				/>
				</div>
				</div>
			)}
			
			{/* Recommended apps */}
			<div className="bg-blue-50 dark:bg-blue-900/30 border
			border-blue-200 dark:border-blue-700 rounded-xl
			p-4 mb-4 text-sm text-blue-800 dark:text-blue-300">
			<p className="font-semibold mb-1">
			Recommended authenticator apps:
			</p>
			<ul className="list-disc list-inside space-y-0.5">
			<li>Google Authenticator (iOS / Android)</li>
			<li>Authy (iOS / Android / Desktop)</li>
			<li>1Password</li>
			<li>Bitwarden</li>
			</ul>
			</div>
			
			{/* Manual entry */}
			<button
			type="button"
			onClick={() => setShowManual(!showManual)}
			className="text-xs text-farm-green dark:text-green-400
			hover:underline mb-4 block mx-auto"
			>
			{showManual ? "Hide" : "Can't scan? Enter key manually"}
			</button>
			
			{showManual && manualKey && (
				<div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4
				border border-gray-200 dark:border-gray-600
				mb-4">
				<p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
				Manual entry key (type this into your app):
				</p>
				<p className="font-mono text-sm text-gray-800 dark:text-gray-100
				break-all text-center tracking-widest">
				{manualKey.match(/.{1,4}/g)?.join(" ")}
				</p>
				<button
				type="button"
				onClick={() => navigator.clipboard.writeText(manualKey)}
				className="text-xs text-farm-green dark:text-green-400
				hover:underline mt-2 block mx-auto"
				>
				Copy key
				</button>
				</div>
			)}
			
			<button
			onClick={() => {
				setError("");
				setStep("verify");
			}}
			className="w-full bg-farm-green text-white py-3 rounded-xl
			font-semibold hover:bg-green-700 transition-colors"
			>
			I've Scanned the Code — Next →
			</button>
			</>
		)}
		
		{step === "verify" && (
			<>
			<div className="text-center mb-5">
			<h2 className="text-2xl font-bold text-farm-green
			dark:text-green-400">
			Confirm Your Code
			</h2>
			<p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
			Enter the 6-digit code currently shown in your authenticator
			app to activate 2FA on your account.
			</p>
			</div>
			
			{error && (
				<div className="bg-red-50 dark:bg-red-900/30 border
				border-red-300 dark:border-red-600 text-red-700
				dark:text-red-300 rounded-lg p-3 mb-4 text-sm">
				{error}
				</div>
			)}
			
			<form onSubmit={handleVerify} className="space-y-4">
			<input
			type="text"
			inputMode="numeric"
			autoComplete="one-time-code"
			required
			maxLength={6}
			value={code}
			onChange={(e) =>
				setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
			}
			placeholder="000000"
			className="w-full border border-gray-300 dark:border-gray-600
			rounded-lg px-3 py-4 text-center text-3xl
			tracking-[0.5em] font-mono bg-white dark:bg-gray-700
			text-gray-800 dark:text-gray-100
			placeholder-gray-300 dark:placeholder-gray-600
			focus:outline-none focus:ring-2
			focus:ring-farm-green"
			/>
			
			<button
			type="submit"
			disabled={loading || code.length !== 6}
			className="w-full bg-farm-green text-white py-3 rounded-xl
			font-semibold hover:bg-green-700 transition-colors
			disabled:opacity-60"
			>
			{loading ? "Verifying…" : "Activate 2FA"}
			</button>
			
			<button
			type="button"
			onClick={() => {
				setStep("qr");
				setCode("");
				setError("");
			}}
			className="w-full border-2 border-gray-300 dark:border-gray-600
			text-gray-600 dark:text-gray-400 py-2 rounded-xl
			hover:bg-gray-50 dark:hover:bg-gray-700
			transition-colors text-sm"
			>
			← Back to QR Code
			</button>
			</form>
			</>
		)}
		</div>
	);
}

function StepDot({ n, active, done }) {
	return (
		<div
		className={`
			h-8 w-8 rounded-full flex items-center justify-center text-sm
			font-bold transition-colors
			${active
				? "bg-farm-green text-white"
				: done
				? "bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400"
				: "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
			}
			`}
			>
			{done ? "✓" : n}
			</div>
	);
}
